import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import PropertyCard from '../components/PropertyCard';
import './ListingsPage.css';

const COORDS = {
  'Germasogeia':  [34.6823, 33.0823],
  'Strovolos':    [35.1280, 33.3620],
  'Coral Bay':    [34.8384, 32.3944],
  'Engomi':       [35.1680, 33.3320],
  'Mackenzie':    [34.8975, 33.6367],
  'Marina':       [34.6700, 33.0480],
  'Limassol':     [34.6841, 33.0392],
  'Nicosia':      [35.1856, 33.3823],
  'Paphos':       [34.7754, 32.4219],
  'Larnaca':      [34.9003, 33.6233],
};

const CYPRUS_BOUNDS = [
  [34.4, 32.0],
  [35.8, 34.7],
];

function PropertyMap({ listings }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (!mapRef.current) return;
    const L = window.L || require('leaflet');
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [34.9, 33.05],
        zoom: 8,
        minZoom: 8,
        maxZoom: 16,
        maxBounds: CYPRUS_BOUNDS,
        maxBoundsViscosity: 1.0,
        zoomControl: false,
      });
      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© OpenStreetMap © CARTO',
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);
      L.control.zoom({ position: 'bottomright' }).addTo(mapInstanceRef.current);
    }
    return () => {};
  }, []);

  useEffect(() => {
    const L = window.L || require('leaflet');
    const map = mapInstanceRef.current;
    if (!map) return;
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    listings.forEach((listing, i) => {
      const coords = COORDS[listing.region] || COORDS[listing.city];
      if (!coords) return;
      const lat = coords[0] + (i % 3 - 1) * 0.005;
      const lng = coords[1] + (i % 2 === 0 ? 0.006 : -0.006);
      const isRent = listing.priceType === 'per month' || listing.priceType === 'rent';
      const price = isRent
        ? '€' + listing.price.toLocaleString() + '/mo'
        : listing.price >= 1000000
          ? '€' + (listing.price / 1000000).toFixed(1) + 'M'
          : '€' + Math.round(listing.price / 1000) + 'K';
      const viewLabel = document.documentElement.lang === 'el' ? 'Δείτε ακίνητο →' : 'View property →';
      const icon = L.divIcon({
        className: '',
        html: `
          <div class="lp-pin-wrap">
            <div class="lp-pin-circle">${price}</div>
            <div class="lp-pin-tooltip">
              <div class="lp-pin-tooltip__title">${listing.title}</div>
              <div class="lp-pin-tooltip__sub">${listing.city}, ${listing.region}</div>
              <div class="lp-pin-tooltip__price">${price}</div>
              <div class="lp-pin-tooltip__cta">${viewLabel}</div>
            </div>
          </div>
        `,
        iconSize: [64, 32],
        iconAnchor: [32, 16],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(map);
      marker.on('click', () => navigate(`/property/${listing.id}`));
      markersRef.current.push(marker);
    });
  }, [listings, navigate]);

  return (
    <div className="lp-map">
      <div ref={mapRef} className="lp-map__container" />
    </div>
  );
}

export default function SavedPage() {
  const { watchlist } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [sortBy, setSortBy] = useState('newest');

  const sorted = [...watchlist].sort((a, b) => {
    if (sortBy === 'price-asc') return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    return 0;
  });

  const countLabel = watchlist.length === 1
    ? t('saved.propertySaved').replace('{n}', watchlist.length)
    : t('saved.propertiesSaved').replace('{n}', watchlist.length);

  return (
    <main className="listings-page">
      <div className="listings-page__header">
        <div className="listings-page__header-inner">
          <h1 className="listings-page__title">{t('saved.title')}</h1>
          <p className="listings-page__count"><strong>{watchlist.length}</strong> {countLabel.replace(/^\d+ /, '')}</p>
        </div>
      </div>

      <div className="listings-page__body">
        <div className="listings-page__filters">
          <div className="filter-group filter-group--right">
            <label>{t('saved.sortBy')}</label>
            <select className="filter-select" value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="newest">{t('saved.savedOrder')}</option>
              <option value="price-asc">{t('listings.filters.priceLow')}</option>
              <option value="price-desc">{t('listings.filters.priceHigh')}</option>
            </select>
          </div>
        </div>

        <div className="listings-page__layout">
          <div className="listings-page__map-col">
            <PropertyMap listings={sorted} />
            {/* Ad slot hidden for now — restore by removing `style={{ display: 'none' }}`. */}
            <div className="lp-ad-slot" style={{ display: 'none' }}>
              <div className="lp-ad-slot__label">{t('saved.advertisement')}</div>
              <div className="lp-ad-slot__title">{t('saved.placeAd')}</div>
              <div className="lp-ad-slot__sub">{t('saved.adSub')}</div>
              <button className="lp-ad-slot__cta">{t('saved.getInTouch')}</button>
            </div>
          </div>
          <div className="listings-page__grid-col">
            {sorted.length > 0 ? (
              <div className="listings-page__grid">
                {sorted.map((listing, i) => (
                  <PropertyCard key={listing.id} listing={listing} index={i} />
                ))}
              </div>
            ) : (
              <div className="listings-page__empty">
                <div className="listings-page__empty-icon">🤍</div>
                <h3>{t('saved.noSaved')}</h3>
                <p>{t('saved.heartProperty')}</p>
                <button className="btn-primary" style={{ marginTop: 20 }} onClick={() => navigate('/listings')}>
                  {t('saved.browseListings')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
